import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { CreditCard, DollarSign, Shield, CheckCircle } from 'lucide-react';
// import PaymentDebug from './PaymentDebug';
import { validateCard, initiatePayment, submitPaymentForm } from '../../services/adumoService';
import PayPointBranding from '../common/PayPointBranding';
import './KPCardRecharge.css';

const RechargeContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #7F1B1C 0%, #640003 100%);
  padding: 120px 20px 80px;
`;

const RechargeContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border: none;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h1`
  color: #1e293b;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 10px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #64748b;
  font-size: 1rem;
  text-align: center;
  margin-bottom: 40px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  color: #334155;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  background: white;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #C31321;
    box-shadow: 0 0 0 3px rgba(195, 19, 33, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const CardTypeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const CardTypeOption = styled.button`
  border: 2px solid ${props => props.$selected ? '#C31321' : '#e2e8f0'};
  border-radius: 14px;
  background: ${props => props.$selected
    ? 'linear-gradient(150deg, rgba(195, 19, 33, 0.08), rgba(255, 255, 255, 1))'
    : 'white'};
  color: #1e293b;
  padding: 14px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: #C31321;
    transform: translateY(-2px);
  }
`;

const CardTypeOptionPreview = styled.div`
  width: 100%;
  border-radius: 12px;
  padding: 14px 12px;
  background: ${props => props.$variant === 'virtual'
    ? 'linear-gradient(135deg, #2f81d9 0%, #2254aa 100%)'
    : 'linear-gradient(135deg, #1f2230 0%, #090c17 100%)'};
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
`;

const CardTypeOptionHeader = styled.div`
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const CardTypeOptionNumber = styled.div`
  color: white;
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 1px;
`;

const CardTypeOptionFooter = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardTypeOptionName = styled.span`
  font-size: 1.05rem;
  font-weight: 700;
`;

const CardTypeIndicator = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected ? '#C31321' : '#cbd5e1'};
  background: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #C31321;
    opacity: ${props => props.$selected ? 1 : 0};
    transform: scale(${props => props.$selected ? 1 : 0.6});
    transition: all 0.2s ease;
  }
`;

const AmountOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const AmountButton = styled.button`
  padding: 12px;
  border: 2px solid ${props => props.$selected ? '#C31321' : '#e2e8f0'};
  background: ${props => props.$selected ? '#C31321' : 'white'};
  color: ${props => props.$selected ? 'white' : '#334155'};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #C31321;
    background: ${props => props.$selected ? '#a3121a' : '#fef2f2'};
  }
`;

const InfoBox = styled.div`
  background: #f8fafc;
  border: 1px solid transparent;
  border-left: 4px solid #C31321;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const InfoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  font-weight: 600;
  margin-bottom: 8px;
`;

const InfoText = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  margin: 4px 0;
  line-height: 1.5;
`;

const SecurityBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 24px;
  color: #166534;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: #C31321;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #a3121a;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(195, 19, 33, 0.3);
  }

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CardNumberPreview = styled.div`
  background: linear-gradient(135deg, #7F1B1C 0%, #640003 100%);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(127, 27, 28, 0.2);
`;

const CardNumberDisplay = styled.div`
  color: white;
  font-family: 'Courier New', monospace;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    letter-spacing: 1px;
  }
`;

const CardTypeLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  text-align: center;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ValidationMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  margin-top: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => props.valid ? `
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  ` : `
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `}
`;

const KPCardRecharge = () => {
  const location = useLocation();
  const parkingPayment = location?.state?.parkingPayment;
  const isParkingPayment = Boolean(parkingPayment?.pricing);
  const [cardType, setCardType] = useState('virtual'); // 'virtual' or 'physical'
  const [lastFiveDigits, setLastFiveDigits] = useState('');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [email, setEmail] = useState('');
  const [currency, setCurrency] = useState('NAD');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingCard, setIsValidatingCard] = useState(false);
  const [cardValidation, setCardValidation] = useState(null); // { valid: boolean, message: string }
  const cardValidationRequestRef = useRef(0);

  const predefinedAmounts = ['50', '100', '200', '500', '1000', '2000'];
  
  // Card prefixes
  const CARD_PREFIXES = {
    virtual: '95162000000',
    physical: '95161000000'
  };

  const cardTypeOptions = [
    { value: 'virtual', label: 'Virtual card', number: '9516 2000 000X' },
    { value: 'physical', label: 'Physical card', number: '9516 1000 000X' }
  ];
  
  // Get full 16-digit card number
  const getFullCardNumber = () => {
    const prefix = CARD_PREFIXES[cardType];
    return prefix + lastFiveDigits;
  };
  
  // Format card number for display (with spaces)
  const formatCardNumberDisplay = () => {
    const prefix = CARD_PREFIXES[cardType];
    const suffix = `${lastFiveDigits}${'•'.repeat(Math.max(0, 5 - lastFiveDigits.length))}`;
    const fullNumber = prefix + suffix;
    // Format as: XXXX XXXX XXXX XXXX
    return fullNumber.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  useEffect(() => {
    if (parkingPayment?.pricing) {
      const payableAmount = String(parkingPayment.pricing.totalPayable);
      setAmount(payableAmount);
      setCustomAmount(payableAmount);
      return;
    }

    const prefill = location?.state?.prefill;
    if (!prefill) {
      return;
    }

    if (prefill.cardNumber) {
      const cardNumberStr = String(prefill.cardNumber).replace(/\s/g, '');
      // Check if it's a virtual or physical card based on prefix
      if (cardNumberStr.startsWith('95162000000')) {
        setCardType('virtual');
        setLastFiveDigits(cardNumberStr.substring(11, 16));
      } else if (cardNumberStr.startsWith('95161000000')) {
        setCardType('physical');
        setLastFiveDigits(cardNumberStr.substring(11, 16));
      } else {
        // If it doesn't match either prefix, just use the last 5 digits
        setLastFiveDigits(cardNumberStr.slice(-5));
      }
    }

    if (prefill.amount) {
      const amountValue = String(prefill.amount);
      setAmount(amountValue);
      setCustomAmount(amountValue);
    }
  }, [location, parkingPayment]);

  // Adumo Online Test Credentials
  const MERCHANT_ID = '9BA5008C-08EE-4286-A349-54AF91A621B0';
  
    // Test Application ID - This is the documented 3D Secure test application
    // From Adumo docs: "3D Secure Test Cards (Application: 23ADADC0-DA2D-4DAC-A128-4845A5D71293)"
    // NOTE: The 4196B0B8 APPLICATION_ID causes routing to virtual-v2 which fails
    const APPLICATION_ID = '23ADADC0-DA2D-4DAC-A128-4845A5D71293';
  
  // Note: In test environment, use the same APPLICATION_ID for all currencies
  // In production, you'll need currency-specific APPLICATION_IDs from your merchant portal
  
  // const JWT_SECRET = 'yglTxLCSMm7PEsfaMszAKf2LSRvM2qVW'; // For future JWT implementation
  
  // Using original API endpoint - test credentials are authorized for this one
  // The gateway endpoint gave 401 Unauthorized, so test creds aren't whitelisted there
  const PAYMENT_URL = 'https://staging-apiv3.adumoonline.com/product/payment/v1/initialisevirtual';

  // For production, use:
  // const PAYMENT_URL = 'https://apiv3.adumoonline.com/product/payment/v1/initialisevirtual';

  const handleAmountSelect = (value) => {
    setAmount(value);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    setAmount(value);
  };
  
  const validateLastFiveDigits = (digits) => {
    return /^\d{5}$/.test(digits);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const getInvalidCardMessage = () => {
    const selectedType = cardType === 'virtual' ? 'virtual' : 'physical';
    return `Invalid ${selectedType} card. Please verify your KP Card number and try again.`;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!lastFiveDigits) {
      newErrors.cardNumber = 'Card number digits are required';
    } else if (!validateLastFiveDigits(lastFiveDigits)) {
      newErrors.cardNumber = 'Please enter exactly last 5 digits of your card number';
    } else if (!cardValidation?.valid) {
      newErrors.cardNumber = 'Card validation is required before payment';
    }

    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Please select or enter a valid amount';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateTransactionID = () => {
    return 'KP' + Date.now() + Math.random().toString(36).substr(2, 9);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    
    if (isValidatingCard) {
      setErrors((prev) => ({
        ...prev,
        cardNumber: 'Card validation in progress. Please wait a moment.'
      }));
      return;
    }

    // Card should already be auto validated
    if (!cardValidation || !cardValidation.valid) {
      setErrors((prev) => ({
        ...prev,
        cardNumber: 'Enter valid last 5 digits to continue'
      }));
      return;
    }

    setIsSubmitting(true);
    
    const fullCardNumber = getFullCardNumber();

    console.log('=== INITIATING PAYMENT WITH JWT ===');
    console.log('Card Type:', cardType);
    console.log('Last Digits:', lastFiveDigits);
    console.log('Full Card Number:', fullCardNumber);
    console.log('Amount:', parseFloat(amount).toFixed(2));
    console.log('Currency:', currency);
    console.log('Email:', email);

    try {
      const baseAmount = parseFloat(amount);
      const amountWithServiceCharge = isParkingPayment ? baseAmount : baseAmount * 1.03;
      
      console.log('Base Amount:', baseAmount.toFixed(2));
      if (!isParkingPayment) {
        console.log('Service Charge (3%):', (baseAmount * 0.03).toFixed(2));
      }
      console.log('Total Amount:', amountWithServiceCharge.toFixed(2));

      // Call backend to get JWT-signed payment parameters
      const paymentData = {
        cardNumber: fullCardNumber,
        amount: amountWithServiceCharge,
        currency: currency,
        email: email,
        tenantId: 'DEFAULT',
        parkingEventId: isParkingPayment ? parkingPayment?.pricing?.eventId : undefined,
        orderNumber: isParkingPayment ? parkingPayment?.pricing?.orderNumber : undefined,
        pan: isParkingPayment ? parkingPayment?.pricing?.pan : undefined,
        ocityRawAmount: isParkingPayment ? parkingPayment?.pricing?.ocityRawAmount : undefined
      };

      console.log('Calling backend API to generate JWT token...');
      const response = await initiatePayment(paymentData);

      console.log('✅ Backend response received:');
      console.log('  Payment Link:', response.paymentLink);
      console.log('  Parameters:', Object.keys(response.paymentParameters));
      console.log('  JWT Token included:', !!response.paymentParameters.Token);

      // Submit form to Adumo with JWT token
      submitPaymentForm(response.paymentLink, response.paymentParameters);

    } catch (error) {
      console.error('❌ Error initiating payment:', error);
      
      let errorMessage = 'Failed to initiate payment. ';
      
      if (error.message.includes('fetch')) {
        errorMessage += 'Cannot connect to backend server. Make sure your Java backend is running.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      setErrors({ submit: errorMessage });
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (lastFiveDigits.length !== 5) {
      setIsValidatingCard(false);
      setCardValidation(null);
      return;
    }

    const requestId = cardValidationRequestRef.current + 1;
    cardValidationRequestRef.current = requestId;
    const fullCardNumber = getFullCardNumber();

    setIsValidatingCard(true);
    setCardValidation(null);

    const timeoutId = setTimeout(async () => {
      try {
        const result = await validateCard(fullCardNumber);
        if (cardValidationRequestRef.current !== requestId) {
          return;
        }

        setCardValidation(result);
        if (!result.valid) {
          const typedMessage = getInvalidCardMessage();
          setCardValidation({
            ...result,
            message: typedMessage
          });
          setErrors((prev) => ({ ...prev, cardNumber: typedMessage }));
        } else {
          setErrors((prev) => {
            const next = { ...prev };
            delete next.cardNumber;
            return next;
          });
        }
      } catch (error) {
        if (cardValidationRequestRef.current !== requestId) {
          return;
        }
        setCardValidation({
          valid: false,
          message: 'Unable to validate card. Please try again.'
        });
      } finally {
        if (cardValidationRequestRef.current === requestId) {
          setIsValidatingCard(false);
        }
      }
    }, 400);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [lastFiveDigits, cardType]);

  return (
    <>
      {/* <PaymentDebug /> */}
      <RechargeContainer>
        <RechargeContent>
        <Title>{isParkingPayment ? 'Pay Parking with KP Card' : 'Recharge KP Card'}</Title>
        <Subtitle>
          {isParkingPayment
            ? 'Complete your KPI parking payment using your virtual or physical KP Card.'
            : 'Add funds to your KP Card securely'}
        </Subtitle>

        <InfoBox>
          <InfoTitle>
            <CreditCard size={20} color="#C31321" />
            {isParkingPayment ? 'About KPI Parking Payment' : 'About KP Card Recharge'}
          </InfoTitle>
          <InfoText>
            {isParkingPayment
              ? 'Pay the calculated parking amount for your open KPI ticket. You will be redirected to a secure payment page to complete the transaction.'
              : 'Recharge your KP Card instantly and enjoy seamless parking payments. Your recharge will be processed immediately after successful payment.'}
          </InfoText>
        </InfoBox>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              Card Type <Required>*</Required>
            </Label>
            <CardTypeOptions>
              {cardTypeOptions.map((option) => (
                <CardTypeOption
                  key={option.value}
                  type="button"
                  $selected={cardType === option.value}
                  onClick={() => {
                    setCardType(option.value);
                    setCardValidation(null);
                  }}
                  aria-pressed={cardType === option.value}
                >
                  <CardTypeOptionPreview $variant={option.value}>
                    <CardTypeOptionHeader>{option.label}</CardTypeOptionHeader>
                    <CardTypeOptionNumber>{option.number}</CardTypeOptionNumber>
                  </CardTypeOptionPreview>
                  <CardTypeOptionFooter>
                    <CardTypeOptionName>{option.label}</CardTypeOptionName>
                    <CardTypeIndicator $selected={cardType === option.value} />
                  </CardTypeOptionFooter>
                </CardTypeOption>
              ))}
            </CardTypeOptions>
          </FormGroup>

          <FormGroup>
            <Label>
              KP Card Number <Required>*</Required>
            </Label>
            <CardNumberPreview>
              <CardTypeLabel>{cardType === 'virtual' ? 'Virtual Card' : 'Physical Card'}</CardTypeLabel>
              <CardNumberDisplay>
                {lastFiveDigits ? formatCardNumberDisplay() : 
                  (cardType === 'virtual' ? '9516 2000 000• ••••' : '9516 1000 000• ••••')}
              </CardNumberDisplay>
            </CardNumberPreview>
            <Input
              type="text"
              placeholder="Enter last 5 digits (e.g., 12345)"
              value={lastFiveDigits}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 5) {
                  setLastFiveDigits(value);
                  setCardValidation(null); // Reset validation when user changes input
                  const newErrors = { ...errors };
                  delete newErrors.cardNumber;
                  setErrors(newErrors);
                }
              }}
              maxLength="5"
              pattern="\d*"
              inputMode="numeric"
            />

            {cardValidation && (
              <ValidationMessage valid={cardValidation.valid}>
                {cardValidation.valid ? (
                  <>
                    <CheckCircle size={18} />
                    {cardValidation.message}
                  </>
                ) : (
                  <>
                    <span>⚠️</span>
                    {cardValidation.message}
                  </>
                )}
              </ValidationMessage>
            )}
            
            {errors.cardNumber && !cardValidation && (
              <ErrorMessage>{errors.cardNumber}</ErrorMessage>
            )}
            
            {isValidatingCard && (
              <ValidationMessage valid={false}>
                <span>⏳</span>
                Validating card...
              </ValidationMessage>
            )}

            <div style={{ marginTop: '8px', color: '#64748b', fontSize: '0.75rem' }}>
              Enter only the last 5 digits of your KP Card number
            </div>
          </FormGroup>

          <FormGroup>
            <Label>
              Email Address <Required>*</Required>
            </Label>
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>
              {isParkingPayment ? 'Parking Amount Due' : 'Select Amount'} <Required>*</Required>
            </Label>
            {!isParkingPayment && (
              <AmountOptions>
                {predefinedAmounts.map((amt) => (
                  <AmountButton
                    key={amt}
                    type="button"
                    $selected={amount === amt}
                    onClick={() => handleAmountSelect(amt)}
                  >
                    {currency} {amt}
                  </AmountButton>
                ))}
              </AmountOptions>
            )}
            <Input
              type="number"
              placeholder={isParkingPayment ? 'Parking amount' : 'Or enter custom amount'}
              value={customAmount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
              min="1"
              step="0.01"
              readOnly={isParkingPayment}
            />
            {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
          </FormGroup>

          <SecurityBadge>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={18} />
              {isParkingPayment
                ? 'Secure KPI parking payment'
                : 'Service fee 3% for Top Up of KP Card'}
            </div>
          </SecurityBadge>

          <PayPointBranding showDivider={false} marginTop="12px" marginBottom="24px" paddingTop="0" />

          <SubmitButton 
            type="submit" 
            disabled={isSubmitting || isValidatingCard || !cardValidation?.valid}
          >
            {isSubmitting ? (
              'Redirecting to Payment...'
            ) : isValidatingCard ? (
              <>
                <Shield size={20} />
                Validating Card...
              </>
            ) : !cardValidation?.valid ? (
              <>
                <Shield size={20} />
                Enter Valid Card
              </>
            ) : (
              <>
                <DollarSign size={20} />
                Proceed to Payment
              </>
            )}
          </SubmitButton>
        </form>

        <InfoBox style={{ marginTop: '24px' }}>
          <InfoTitle>
            <CheckCircle size={20} color="#C31321" />
            Payment Information
          </InfoTitle>
          <InfoText>
            • You will be redirected to a secure payment page
          </InfoText>
          <InfoText>
            • All payments are processed through 3D Secure authentication
          </InfoText>
          <InfoText>
            • {isParkingPayment
              ? 'Your KPI parking payment will be processed after successful authorisation'
              : 'Your card will be recharged immediately upon successful payment'}
          </InfoText>
          <InfoText>
            • You will receive a confirmation email after the transaction
          </InfoText>
        </InfoBox>
      </RechargeContent>
    </RechargeContainer>
    </>
  );
};

export default KPCardRecharge;

